import React, { useState, useEffect } from 'react';
import { Container, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { ScannerDashboard } from '../../types';
import SkeletonLoader from '../../components/common/SkeletonLoader';
import './ScannerDashboardPage.scss';

const ScannerDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<ScannerDashboard | null>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.scanner.getDashboard();
        setDashboardData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load scanner dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="scanner-dashboard-page">
        <Container className="py-4">
          <SkeletonLoader />
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scanner-dashboard-page">
        <Container className="py-4">
          <Alert variant="danger">{error}</Alert>
        </Container>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <div className="scanner-dashboard-page">
        <Container className="py-2">
          <div className="scanner-dashboard">
            <div className="dashboard-header">
              <div className="header-content">
                <div className="header-icon">
                  <i className="fas fa-qrcode"></i>
                </div>
                <div className="header-text">
                  <h1 className="text-primary mb-1">Scanner Dashboard</h1>
                  <p className="text-muted small mb-0">Manage and scan tickets for your events and activities</p>
                </div>
              </div>
            </div>

            {dashboardData.events.length === 0 && dashboardData.activity_places.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-search"></i>
                <h3>No Scanner Access</h3>
                <p>You don't have scanner access to any events or activities yet.</p>
              </div>
            )}

            {dashboardData.events.length > 0 && (
              <>
                <div className="section-header">
                  <div className="section-header-content">
                    <div className="section-icon">
                      <i className="fas fa-calendar-alt"></i>
                    </div>
                    <h2 className="section-title">Events</h2>
                    <span className="section-count">{dashboardData.events.length}</span>
                  </div>
                </div>
                <div className="scanner-grid mb-3">
                  {dashboardData.events.map((event) => (
                    <div key={event.id} className="scanner-card">
                      <div className="card-header">
                        {event.banner_url && (
                          <img 
                            src={event.banner_url} 
                            alt={event.title}
                            className="thumbnail"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (event.thumbnail_url) {
                                target.src = event.thumbnail_url;
                              } else {
                                target.style.display = 'none';
                              }
                            }}
                          />
                        )}
                        {!event.banner_url && event.thumbnail_url && (
                          <img 
                            src={event.thumbnail_url} 
                            alt={event.title}
                            className="thumbnail"
                          />
                        )}
                        <h3 className="title">{event.title}</h3>
                      </div>
                      <div className="card-body">
                        <div className="scanner-options">
                          <a 
                            href="#" 
                            className="scanner-option"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/scanner/event/${event.slug}/qr/`);
                            }}
                          >
                            <i className="fas fa-qrcode"></i>
                            <span>QR</span>
                          </a>
                          <a 
                            href="#" 
                            className="scanner-option"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/scanner/event/${event.slug}/stats/`);
                            }}
                          >
                            <i className="fas fa-chart-bar"></i>
                            <span>Stats</span>
                          </a>
                          <a 
                            href="#" 
                            className="scanner-option"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/scanner/event/${event.slug}/attendees/`);
                            }}
                          >
                            <i className="fas fa-users"></i>
                            <span>Attendees</span>
                          </a>
                          <a 
                            href="#" 
                            className="scanner-option"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/scanner/event/${event.slug}/addon/`);
                            }}
                          >
                            <i className="fas fa-plus-circle"></i>
                            <span>Add-on</span>
                          </a>
                        </div>
                      </div>
                      <div className="card-footer">
                        {event.start_date && (
                          <span className="status">
                            <i className="fas fa-calendar me-1"></i>
                            {new Date(event.start_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {dashboardData.activity_places.length > 0 && (
              <>
                <div className="section-header">
                  <div className="section-header-content">
                    <div className="section-icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <h2 className="section-title">Activities</h2>
                    <span className="section-count">{dashboardData.activity_places.length}</span>
                  </div>
                </div>
                <div className="scanner-grid">
                  {dashboardData.activity_places.map((place) => (
                    <div key={place.id} className="scanner-card">
                      <div className="card-header">
                        {place.thumbnail_url && (
                          <img 
                            src={place.thumbnail_url} 
                            alt={place.name}
                            className="thumbnail"
                          />
                        )}
                        <h3 className="title">{place.name}</h3>
                      </div>
                      <div className="card-body">
                        <div className="scanner-options">
                          <a 
                            href="#" 
                            className="scanner-option"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/scanner/activity/${place.slug}/qr/`);
                            }}
                          >
                            <i className="fas fa-qrcode"></i>
                            <span>QR Scanner</span>
                          </a>
                        </div>
                      </div>
                      <div className="card-footer">
                        {place.location && (
                          <span className="location">
                            <i className="fas fa-map-pin"></i>
                            {place.location}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Container>
    </div>
  );
};

export default ScannerDashboardPage;
